const getApiBase = () => {
  const envUrl = process.env.REACT_APP_PUBLIC_API_BASE_URL || process.env.REACT_APP_KAY_API_BASE_URL;

  return (envUrl || "https://backend-api.konn3ct.ng").replace(/\/+$/, "");
};

const API_BASE = getApiBase();
const PAYSTACK_INLINE_SRC = "https://js.paystack.co/v2/inline.js";

const parseJsonSafely = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const makeApiError = (response, body, fallbackMessage) => {
  const error = new Error(body?.message || fallbackMessage || `Request failed (HTTP ${response.status}).`);

  error.status = response.status;
  error.body = body;
  return error;
};

export const registerFreeEvent = async ({ ref, name, email, phone }) => {
  const payload = {
    ref: String(ref || "").trim(),
    name: String(name || "").trim(),
    email: String(email || "").trim(),
    phone: String(phone || "").trim(),
  };

  if (!payload.ref) {
    throw new Error("Event reference is required.");
  }

  if (!payload.name || !payload.email || !payload.phone) {
    throw new Error("Name, email and phone number are required.");
  }

  let response;

  try {
    response = await fetch(`${API_BASE}/api/pre-registration`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
  } catch (cause) {
    const error = new Error("Unable to reach the Konn3ct event service. Please check your connection and try again.");
    error.cause = cause;
    throw error;
  }

  const body = await parseJsonSafely(response);

  if (!response.ok || body?.success === false) {
    const error = makeApiError(response, body, "Unable to register for this event.");

    if (response.status === 400 && /already|register for this event already/i.test(body?.message || "")) {
      error.code = "ALREADY_REGISTERED";
    }

    throw error;
  }

  return body;
};

export const initializePaidEventCheckout = async ({ eventId, name, email, phone }) => {
  const numericEventId = Number(eventId);
  const cleanName = String(name || "").trim();
  const cleanEmail = String(email || "").trim();
  const cleanPhone = String(phone || "").trim();

  if (!Number.isFinite(numericEventId) || numericEventId <= 0) {
    const error = new Error("This paid event is missing the event ID required for checkout.");
    error.code = "EVENT_ID_REQUIRED";
    throw error;
  }

  if (!cleanName || !cleanEmail || !cleanPhone) {
    throw new Error("Name, email and phone number are required.");
  }

  let response;

  try {
    response = await fetch(`${API_BASE}/api/v1/checkout/initialize`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        event_id: numericEventId,
        name: cleanName,
        email: cleanEmail,
        phone: cleanPhone,
        provider: "paystack",
      }),
    });
  } catch (cause) {
    const error = new Error("Unable to start Paystack checkout. Please check your connection and try again.");
    error.cause = cause;
    throw error;
  }

  const body = await parseJsonSafely(response);

  if (!response.ok) {
    const error = makeApiError(response, body, "Unable to initialize Paystack checkout.");

    if (response.status === 422 && /already paid/i.test(body?.message || "")) {
      error.code = "ALREADY_PAID";
    }

    throw error;
  }

  if (!body?.access_code) {
    const error = new Error("Checkout initialized but the server did not return a Paystack access code.");
    error.body = body;
    throw error;
  }

  if (body?.provider && String(body.provider).toLowerCase() !== "paystack") {
    const error = new Error("The checkout provider returned by the server is not Paystack.");
    error.body = body;
    throw error;
  }

  return {
    accessCode: body.access_code,
    paymentUrl: body.payment_url || null,
    localReference: body.local_reference || null,
    ticketNumber: body.ticket_number || null,
    provider: "paystack",
    raw: body,
  };
};

export const verifyPaidEventCheckout = async ({ reference }) => {
  const cleanRef = String(reference || "").trim();

  if (!cleanRef) {
    throw new Error("Transaction reference is required for verification.");
  }

  let response;

  try {
    response = await fetch(`${API_BASE}/api/v1/checkout/verify`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ reference: cleanRef }),
    });
  } catch (cause) {
    const error = new Error("Unable to reach the Konn3ct verification service. Please check your connection.");
    error.cause = cause;
    throw error;
  }

  const body = await parseJsonSafely(response);

  if (!response.ok || body?.success === false) {
    throw makeApiError(response, body, "Payment verification failed.");
  }

  return body;
};

let paystackLoaderPromise = null;

export const preloadPaystackInline = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Paystack checkout is only available in a browser."));
  }

  if (window.PaystackPop) {
    return Promise.resolve(window.PaystackPop);
  }

  if (paystackLoaderPromise) {
    return paystackLoaderPromise;
  }

  paystackLoaderPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${PAYSTACK_INLINE_SRC}"]`);

    const finish = () => {
      if (window.PaystackPop) {
        resolve(window.PaystackPop);
      } else {
        paystackLoaderPromise = null;
        reject(new Error("Paystack checkout library did not load correctly."));
      }
    };

    if (existing) {
      if (window.PaystackPop) {
        finish();
        return;
      }

      existing.addEventListener("load", finish, { once: true });
      existing.addEventListener(
        "error",
        () => {
          paystackLoaderPromise = null;
          reject(new Error("Unable to load Paystack checkout."));
        },
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.src = PAYSTACK_INLINE_SRC;
    script.async = true;
    script.onload = finish;
    script.onerror = () => {
      paystackLoaderPromise = null;
      reject(new Error("Unable to load Paystack checkout."));
    };

    document.head.appendChild(script);
  });

  return paystackLoaderPromise;
};

export const openPaystackPopup = async ({ accessCode, onSuccess, onCancel, onError, onLoad }) => {
  if (!accessCode) {
    throw new Error("Paystack access code is missing.");
  }

  const PaystackPop = await preloadPaystackInline();
  const popup = new PaystackPop();

  return popup.resumeTransaction(accessCode, {
    onSuccess: (transaction) => {
      onSuccess?.(transaction);
    },
    onCancel: () => {
      onCancel?.();
    },
    onError: (error) => {
      onError?.(error);
    },
    onLoad: (response) => {
      onLoad?.(response);
    },
  });
};

// const getApiBase = () => {
//   const envUrl =
//     process.env.REACT_APP_PUBLIC_API_BASE_URL ||
//     process.env.REACT_APP_KAY_API_BASE_URL;

//   return (envUrl || "https://backend-api.konn3ct.ng").replace(/\/+$/, "");
// };

// const API_BASE = getApiBase();

// const parseJsonSafely = async (response) => {
//   try {
//     return await response.json();
//   } catch {
//     return null;
//   }
// };

// const makePaidReference = (eventRef) => {
//   const randomPart = Math.random().toString(36).slice(-6).toUpperCase();
//   return `KONN-${eventRef}-${Date.now()}-${randomPart}`;
// };

// const extractPaymentUrl = (body) => {
//   const candidates = [
//     body?.payment_url,
//     body?.authorization_url,
//     body?.data?.payment_url,
//     body?.data?.authorization_url,
//     typeof body?.data === "string" ? body.data : null,
//   ];

//   for (const candidate of candidates) {
//     if (typeof candidate !== "string" || !candidate.trim()) {
//       continue;
//     }

//     try {
//       const parsed = new URL(candidate);
//       const host = parsed.hostname.toLowerCase();
//       const isPaystack = host === "paystack.com" || host.endsWith(".paystack.com");

//       if (parsed.protocol === "https:" && isPaystack) {
//         return parsed.toString();
//       }
//     } catch {
//       // Ignore malformed candidates and continue checking.
//     }
//   }

//   return null;
// };

// /**
//  * One public event-registration flow for both free and paid events.
//  *
//  * Free:
//  *   POST /api/pre-registration -> registration success
//  *
//  * Paid:
//  *   POST /api/pre-registration -> registration success + Paystack checkout URL
//  *
//  * There is intentionally no frontend login gate and no payment-provider selector.
//  */
// export const registerForEvent = async ({
//   ref,
//   name,
//   email,
//   phone,
//   paid = false,
// }) => {
//   const cleanRef = String(ref || "").trim();
//   const cleanName = String(name || "").trim();
//   const cleanEmail = String(email || "").trim();
//   const cleanPhone = String(phone || "").trim();

//   if (!cleanRef) {
//     throw new Error("Event reference is required.");
//   }

//   if (!cleanName || !cleanEmail || !cleanPhone) {
//     throw new Error("Name, email and phone number are required.");
//   }

//   const payload = {
//     ref: cleanRef,
//     name: cleanName,
//     email: cleanEmail,
//     phone: cleanPhone,
//   };

//   // The proven paid-event flow supplies a unique transaction reference during
//   // pre-registration so the backend can initialize the Paystack transaction.
//   if (paid) {
//     payload.reference = makePaidReference(cleanRef);
//   }

//   let response;

//   try {
//     response = await fetch(`${API_BASE}/api/pre-registration`, {
//       method: "POST",
//       headers: {
//         Accept: "application/json",
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify(payload),
//     });
//   } catch (networkError) {
//     const error = new Error(
//       "Unable to reach the Konn3ct event service. Check the API connection/CORS configuration and try again.",
//     );
//     error.cause = networkError;
//     throw error;
//   }

//   const body = await parseJsonSafely(response);

//   if (!response.ok || body?.success === false) {
//     const error = new Error(
//       body?.message || `Event registration failed (HTTP ${response.status}).`,
//     );
//     error.status = response.status;
//     error.body = body;
//     throw error;
//   }

//   if (!body?.success) {
//     throw new Error(body?.message || "Event registration was not confirmed by the server.");
//   }

//   const paymentUrl = paid ? extractPaymentUrl(body) : null;

//   if (paid && !paymentUrl) {
//     const error = new Error(
//       "Registration was accepted, but the server did not return a valid Paystack checkout URL.",
//     );
//     error.status = response.status;
//     error.body = body;
//     throw error;
//   }

//   return {
//     ...body,
//     paymentUrl,
//   };
// };

// const API_BASE =
//   (process.env.REACT_APP_KAY_API_BASE_URL ||
//     process.env.REACT_APP_PUBLIC_API_BASE_URL ||
//     "https://backend-api.konn3ct.ng")
//     .replace(/\/+$/, "");

// const parseJsonSafely = async (response) => {
//   try {
//     return await response.json();
//   } catch {
//     return null;
//   }
// };

// const makeHttpError = (message, status, body) => {
//   const error = new Error(message);
//   error.status = status;
//   error.body = body;
//   return error;
// };

// export const registerForFreeEvent = async ({
//   ref,
//   name,
//   email,
//   phone,
// }) => {
//   const payload = {
//     ref: String(ref || "").trim(),
//     name: String(name || "").trim(),
//     email: String(email || "").trim(),
//     phone: String(phone || "").trim(),
//   };

//   if (!payload.ref) {
//     throw new Error("Event reference is required.");
//   }

//   if (!payload.name || !payload.email || !payload.phone) {
//     throw new Error("Name, email and phone are required.");
//   }

//   let response;

//   try {
//     response = await fetch(`${API_BASE}/api/pre-registration`, {
//       method: "POST",
//       headers: {
//         Accept: "application/json",
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify(payload),
//     });
//   } catch (networkError) {
//     const error = new Error(
//       "Could not reach the Konn3ct registration API. Check the API host/CORS configuration and try again."
//     );
//     error.code = "NETWORK_ERROR";
//     error.cause = networkError;
//     throw error;
//   }

//   const body = await parseJsonSafely(response);

//   if (!response.ok || body?.success !== true) {
//     throw makeHttpError(
//       body?.message || `Registration failed with HTTP ${response.status}.`,
//       response.status,
//       body
//     );
//   }

//   return body;
// };

// export const initializePaidEventCheckout = async ({
//   eventId,
//   authToken,
// }) => {
//   const numericEventId = Number(eventId);

//   if (!Number.isInteger(numericEventId) || numericEventId <= 0) {
//     const error = new Error(
//       "This paid event does not expose the numeric event_id required for Paystack checkout."
//     );
//     error.code = "EVENT_ID_REQUIRED";
//     throw error;
//   }

//   if (!authToken) {
//     const error = new Error(
//       "Authentication is required before a paid ticket can be purchased."
//     );
//     error.code = "AUTH_REQUIRED";
//     throw error;
//   }

//   let response;

//   try {
//     response = await fetch(`${API_BASE}/api/v1/checkout/initialize`, {
//       method: "POST",
//       headers: {
//         Accept: "application/json",
//         "Content-Type": "application/json",
//         Authorization: `Bearer ${authToken}`,
//       },
//       body: JSON.stringify({
//         event_id: numericEventId,
//         provider: "paystack",
//       }),
//     });
//   } catch (networkError) {
//     const error = new Error(
//       "Could not reach the Konn3ct payment API. Check the API host/CORS configuration and try again."
//     );
//     error.code = "NETWORK_ERROR";
//     error.cause = networkError;
//     throw error;
//   }

//   const body = await parseJsonSafely(response);

//   if (!response.ok) {
//     throw makeHttpError(
//       body?.message || `Checkout failed with HTTP ${response.status}.`,
//       response.status,
//       body
//     );
//   }

//   if (!body?.payment_url || !body?.local_reference) {
//     throw new Error(
//       "Paystack checkout response did not include payment_url and local_reference."
//     );
//   }

//   if (body?.provider && String(body.provider).toLowerCase() !== "paystack") {
//     throw new Error(
//       "Checkout provider mismatch. Expected Paystack."
//     );
//   }

//   return body;
// };

// export const getExistingAuthToken = () => {
//   if (typeof window === "undefined") return null;

//   const storageKey =
//     process.env.REACT_APP_AUTH_TOKEN_STORAGE_KEY || "konn3ct_auth_token";

//   return (
//     window.sessionStorage.getItem(storageKey) ||
//     window.localStorage.getItem(storageKey) ||
//     null
//   );
// };

// const PUBLIC_API_BASE =
//   process.env.REACT_APP_PUBLIC_API_BASE_URL || "https://backend-api.konn3ct.ng/";

// const KAY_API_BASE =
//   process.env.REACT_APP_KAY_API_BASE_URL || "https://backend-api.konn3ct.ng/";

// const parseJsonSafely = async (response) => {
//   try {
//     return await response.json();
//   } catch {
//     return null;
//   }
// };

// const makeHttpError = (message, status, body) => {
//   const error = new Error(message);
//   error.status = status;
//   error.body = body;
//   return error;
// };

// // export const registerForFreeEvent = async ({
// //   ref,
// //   name,
// //   email,
// //   phone,
// // }) => {
// //   const response = await fetch(`${PUBLIC_API_BASE}/pre-registration`, {
// //     method: "POST",
// //     headers: {
// //       Accept: "application/json",
// //       "Content-Type": "application/json",
// //     },
// //     body: JSON.stringify({
// //       ref,
// //       name,
// //       email,
// //       phone,
// //     }),
// //   });

// //   const body = await parseJsonSafely(response);

// //   if (!response.ok || !body?.success) {
// //     throw makeHttpError(
// //       body?.message || "Event registration failed.",
// //       response.status,
// //       body
// //     );
// //   }

// //   return body;
// // };
// export const registerForFreeEvent =
//   async ({
//     ref,
//     name,
//     email,
//     phone,
//   }) => {

//     const cleanRef =
//       String(ref || "").trim();

//     if (!cleanRef) {
//       throw new Error(
//         "Event reference is required."
//       );
//     }

//     const formData =
//       new FormData();

//     formData.append(
//       "ref",
//       cleanRef
//     );

//     formData.append(
//       "name",
//       String(name || "").trim()
//     );

//     formData.append(
//       "email",
//       String(email || "").trim()
//     );

//     formData.append(
//       "phone",
//       String(phone || "").trim()
//     );

//     console.log(
//       "Submitting registration:",
//       {
//         ref: cleanRef,
//         name,
//         email,
//         phone,
//       }
//     );

//     const response =
//       await fetch(
//         `${PUBLIC_API_BASE}/pre-registration`,
//         {
//           method: "POST",

//           headers: {
//             Accept:
//               "application/json",
//           },

//           body: formData,
//         }
//       );

//     let body = null;

//     try {
//       body =
//         await response.json();
//     } catch {
//       // response was not JSON
//     }

//     if (
//       !response.ok ||
//       !body?.success
//     ) {
//       const error =
//         new Error(
//           body?.message ||
//           "Registration failed."
//         );

//       error.status =
//         response.status;

//       error.body = body;

//       throw error;
//     }

//     return body;
//   };

// export const initializePaidEventCheckout = async ({
//   eventRef,
//   provider,
//   authToken,
// }) => {
//   if (!eventRef) {
//     throw new Error(
//       "This event does not expose the event_ref required by secure checkout."
//     );
//   }

//   if (!authToken) {
//     const error = new Error(
//       "Authentication is required before a paid ticket can be purchased."
//     );
//     error.code = "AUTH_REQUIRED";
//     throw error;
//   }

//   const response = await fetch(`${KAY_API_BASE}/api/v1/checkout/initialize`, {
//     method: "POST",
//     headers: {
//       Accept: "application/json",
//       "Content-Type": "application/json",
//       Authorization: `Bearer ${authToken}`,
//     },
//     body: JSON.stringify({
//       event_ref: eventRef,
//       provider,
//     }),
//   });

//   const body = await parseJsonSafely(response);

//   if (!response.ok) {
//     throw makeHttpError(
//       body?.message || `Checkout failed with HTTP ${response.status}`,
//       response.status,
//       body
//     );
//   }

//   if (!body?.payment_url || !body?.local_reference) {
//     throw new Error(
//       "Checkout response did not include payment_url and local_reference."
//     );
//   }

//   return body;
// };

// export const getExistingAuthToken = () => {
//   if (typeof window === "undefined") return null;

//   const storageKey =
//     process.env.REACT_APP_AUTH_TOKEN_STORAGE_KEY || "konn3ct_auth_token";

//   return (
//     window.sessionStorage.getItem(storageKey) ||
//     window.localStorage.getItem(storageKey) ||
//     null
//   );
// };
